import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, User, LogIn } from 'lucide-react';
import LoginLayout from '../components/LoginLayout';
import { showToastSuccess, showToastError } from '../utils/toastUtils';
import { useLogin } from '../hooks/useAuth';
import { useSimpleLanguage } from '../hooks/useSimpleLanguage';

interface LoginForm {
  username: string;
  password: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use lightweight language hook (no Redux) for login page optimization
  const { t, i18n } = useSimpleLanguage('login');
  
  // React Query mutation for login
  const loginMutation = useLogin();
  
  const [formData, setFormData] = useState<LoginForm>({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || '/';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Prevent any angle brackets (< or >) which are used in HTML/script tags
    // This will block <scrip>, <script>, or any HTML tags from being typed
    if (value.includes('<') || value.includes('>')) {
      toast.error(t('invalidInput'));
      return; // Don't update the state if angle brackets are detected
    }
    
    // Additional dangerous patterns check
    const dangerousPatterns = [
      /javascript:/gi,
      /on\w+\s*=/gi, // onclick, onerror, etc.
      /eval\(/gi,
      /expression\(/gi
    ];
    
    // Check if value contains any dangerous patterns
    const containsScripts = dangerousPatterns.some(pattern => pattern.test(value));
    
    if (containsScripts) {
      toast.error(t('invalidInput'));
      return; // Don't update the state if dangerous content is detected
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
      toast.dismiss();
    }
  };

  const handleLoginError = (messageKey: string) => {
    showToastError(t, messageKey, i18n);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError(t('fillAllFields'));
      return;
    }

    setError('');
    setSuccess('');

    // Use React Query mutation
    loginMutation.mutate(formData, {
      onSuccess: (data) => {
        if (data.success && data.data) {
          // Show success toast
          showToastSuccess(t, 'loginSuccess', i18n);
          setSuccess(t('redirecting'));
          
          // Redirect based on user role
          setTimeout(() => {
            if (data.data?.user?.adminLevel === 'SUPER_ADMIN') {
              navigate('/dashboard', { replace: true });
            } else if (data.data?.user?.adminLevel === 'OWNER') {
              navigate('/owner-dashboard', { replace: true });
            } else if (data.data?.user?.adminLevel === 'BRANCH_MANAGER') {
              navigate('/branch-manager-dashboard', { replace: true });
            } else {
              navigate(from, { replace: true });
            }
          }, 1000);
        }
      },
      onError: (error: any) => {
        const errorMessage = error.message || t('loginFailed');
        setError(errorMessage);
        handleLoginError('loginFailed');
      },
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginLayout>
      <div className="w-full backdrop-blur-xl bg-white/30 dark:bg-gray-900/30 rounded-2xl shadow-2xl p-8 border border-white/50 dark:border-gray-700/50">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-14 h-14 backdrop-blur-md bg-sky-500/20 rounded-full flex items-center justify-center border border-sky-400/50 shadow-lg">
              <Lock className="h-7 w-7 text-sky-400" />
            </div>
            <h1 className="text-4xl font-bold text-white drop-shadow-lg">{t('signIn')}</h1>
          </div>
          <p className="text-gray-200 text-sm drop-shadow-md">{t('enterCredentials')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Username Field */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-white mb-2 drop-shadow-md">
              {t('usernameOrEmail')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-3 rtl:pl-0 rtl:pr-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-300" />
              </div>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className="block w-full pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-3 backdrop-blur-md bg-white/20 rounded-lg border border-white/40 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-400 transition-all text-white placeholder-gray-300 rtl:text-right shadow-sm"
                placeholder={t('enterUsername')}
                disabled={loginMutation.isPending}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white mb-2 drop-shadow-md">
              {t('password')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-3 rtl:pl-0 rtl:pr-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-300" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleInputChange}
                className="block w-full pl-10 rtl:pl-10 pr-10 rtl:pr-10 py-3 backdrop-blur-md bg-white/20 rounded-lg border border-white/40 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-400 transition-all text-white placeholder-gray-300 rtl:text-right shadow-sm"
                placeholder={t('enterPassword')}
                disabled={loginMutation.isPending}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 pr-3 rtl:pr-0 rtl:pl-3 flex items-center text-gray-300 hover:text-white transition-colors z-10"
                disabled={loginMutation.isPending}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
              {success}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full py-3 px-4 backdrop-blur-md bg-sky-950/90 hover:bg-sky-900/95 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-none border border-sky-700/40"
          >
            {loginMutation.isPending ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 rtl:mr-0 rtl:ml-2"></div>
                {t('signingIn')}
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <LogIn className="h-5 w-5 mr-2 rtl:mr-0 rtl:ml-2" />
                {t('signIn')}
              </div>
            )}
          </button>
        </form>
      </div>
    </LoginLayout>
  );
};

// Enable why-did-you-render tracking for this component
(LoginPage as any).whyDidYouRender = true;

export default LoginPage;
